import React, { useState } from 'react';
import { login, resetPassword } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Lock, User } from 'lucide-react';

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetForm, setResetForm] = useState({
    username: '',
    security_answer: '',
    new_password: '',
    confirm_password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await login(username, password);
      toast.success('Giriş başarılı!');
      onLogin(data.access_token, data.user);
    } catch (error) {
      toast.error('Giriş başarısız. Lütfen bilgilerinizi kontrol edin.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (resetForm.new_password !== resetForm.confirm_password) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }

    if (resetForm.new_password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }

    try {
      await resetPassword(
        resetForm.username,
        resetForm.security_answer,
        resetForm.new_password
      );
      toast.success('Şifreniz başarıyla sıfırlandı. Giriş yapabilirsiniz.');
      setShowResetDialog(false);
      setResetForm({
        username: '',
        security_answer: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error('Şifre sıfırlama başarısız. Kullanıcı adı veya güvenlik cevabı hatalı olabilir.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="glass-effect rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <span className="text-3xl font-bold text-white">P</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800 }}>ProGo Mobil Sistemler</h1>
            <p className="text-gray-600" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>Müşteri Ziyaret ve Satış Takip Sistemi</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-gray-700 font-medium">Kullanıcı Adı</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Kullanıcı adınız"
                  className="pl-11 h-12 bg-white border-gray-200 focus:border-blue-400"
                  required
                  data-testid="login-username-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 font-medium">Şifre</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Şifreniz"
                  className="pl-11 h-12 bg-white border-gray-200 focus:border-blue-400"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg transition-all"
              data-testid="login-submit-button"
            >
              {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </Button>

            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => setShowResetDialog(true)}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium underline"
              >
                Şifremi Unuttum
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Şifre Sıfırlama Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Şifre Sıfırlama</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label>Kullanıcı Adı</Label>
              <Input
                value={resetForm.username}
                onChange={(e) => setResetForm({ ...resetForm, username: e.target.value })}
                placeholder="Kullanıcı adınız"
                required
              />
            </div>
            <div>
              <Label>Güvenlik Cevabı</Label>
              <Input
                value={resetForm.security_answer}
                onChange={(e) => setResetForm({ ...resetForm, security_answer: e.target.value })}
                placeholder="Güvenlik cevabınız"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Not: Güvenlik cevabı olarak doğum tarihiniz kullanılmaktadır (GGAAYYYY formatında)
              </p>
            </div>
            <div>
              <Label>Yeni Şifre</Label>
              <Input
                type="password"
                value={resetForm.new_password}
                onChange={(e) => setResetForm({ ...resetForm, new_password: e.target.value })}
                placeholder="Yeni şifreniz"
                required
                minLength={6}
              />
            </div>
            <div>
              <Label>Yeni Şifre (Tekrar)</Label>
              <Input
                type="password"
                value={resetForm.confirm_password}
                onChange={(e) => setResetForm({ ...resetForm, confirm_password: e.target.value })}
                placeholder="Yeni şifrenizi tekrar girin"
                required
                minLength={6}
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowResetDialog(false);
                  setResetForm({ username: '', security_answer: '', new_password: '', confirm_password: '' });
                }}
              >
                İptal
              </Button>
              <Button type="submit" className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white">
                Şifreyi Sıfırla
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;